import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { X, Save, Settings2, Trash2, ArrowLeft, Search, ChevronDown, Eye, Edit3, Plus, AlertCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import CategorySelector from '../components/CategorySelector';
import AttributeSetSelector from '../components/AttributeSetSelector';
import ImageUpload from '../components/ImageUpload';
import EavAttributeField from '../components/EavAttributeField';
import EavGroup from '../components/EavGroup';
import SupplierSelect from '../components/SupplierSelect';
import { findFirstEavError } from '../utils/attributeValidation';

const ProductForm = ({ viewOnly = false }) => {
  const { id } = useParams();
  const isEdit = !!id;
  const { t, lang } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();

  const getLocalizedLabel = (attr) => {
    let label = '';
    if (attr.labels && Array.isArray(attr.labels)) {
      const match = attr.labels.find(l => l.locale === lang);
      if (match && match.label) {
        label = match.label;
      }
    }
    if (!label) {
      label = attr.default_label || attr.attribute_code || '';
    }
    // Remove trailing digits from any label source
    label = label.replace(/\d+$/, '');
    return label;
  };
  const [config, setConfig] = useState([]);
  const [formData, setFormData] = useState({
    sku: '',
    category_id: '',
    category_name: '',
    attribute_set_id: 1,
    item_group_id: '',
    supplier_id: '',
    supplier_name: '',
    attributes: {},
    images: [],
  });
  const [viewOnlyMode, setViewOnlyMode] = useState(viewOnly);
  // Sync viewOnly prop to state when navigating between edit/view routes (React Router may reuse component instance)
  useEffect(() => {
    setViewOnlyMode(viewOnly);
  }, [viewOnly]);
  const [skuError, setSkuError] = useState('');
  const [skuChecking, setSkuChecking] = useState(false);
  const [attributeSetLocked, setAttributeSetLocked] = useState(true);
  const [loading, setLoading] = useState(true);

  // Variant state
  const [showVariantConfig, setShowVariantConfig] = useState(false);
  const [showAddVariants, setShowAddVariants] = useState(false);
  const [showUpdateVariantAttrs, setShowUpdateVariantAttrs] = useState(false);
  const [selectedUpdateAttrs, setSelectedUpdateAttrs] = useState([]); // full list including existing + new
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedAddProductIds, setSelectedAddProductIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [variantDefiningAttrs, setVariantDefiningAttrs] = useState([]); // { code: 'color', label: 'Color' }
  const [variantAttrOptions, setVariantAttrOptions] = useState({}); // { color: [{option_id, value, label, swatch_value, swatch_type}, ...] }
  const [variantValues, setVariantValues] = useState({}); // { color: ['option_value_1', 'option_value_2'], size: [...] }
  const [combinations, setCombinations] = useState([]);
  const [variantSearch, setVariantSearch] = useState('');
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const originalChildIdsRef = useRef(new Set()); // existing child IDs on edit load

  // Variant image upload state
  const [variantImageAttr, setVariantImageAttr] = useState('');
  const [variantImages, setVariantImages] = useState({}); // { [option_value]: [images] }
  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // collapsible EAV attribute groups
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setVariantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load predefined options for selected variant-defining attributes
  useEffect(() => {
    const codes = variantDefiningAttrs.map(a => a.code);
    const newOptions = { ...variantAttrOptions };
    let changed = false;

    codes.forEach(code => {
      if (newOptions[code]) return; // already loaded
      const attr = config.find(a => a.attribute_code === code);
      if (!attr || !attr.attribute_id) return;

      changed = true;
      newOptions[code] = null; // mark as loading

      fetch(`/api/settings/attributes/${attr.attribute_id}/options`)
        .then(res => res.json())
        .then(res => {
          if (res.status === 'success' && res.data) {
            // Parse label from locale or default_label
            const parsed = res.data.map(opt => ({
              ...opt,
              label: getOptionLabel(opt, attr, lang)
            }));
            setVariantAttrOptions(prev => ({ ...prev, [code]: parsed }));
          } else {
            setVariantAttrOptions(prev => ({ ...prev, [code]: [] }));
          }
        })
        .catch(() => {
          setVariantAttrOptions(prev => ({ ...prev, [code]: [] }));
        });
    });

    if (changed) {
      setVariantAttrOptions(newOptions);
    }
  }, [variantDefiningAttrs, config, lang]);

  // Get localized label for an option
  const getOptionLabel = (opt, attr, locale) => {
    // API returns english_label and chinese_label directly
    if (locale === 'en' && opt.english_label) return opt.english_label;
    if (locale === 'zh' && opt.chinese_label) return opt.chinese_label;
    return opt.label || opt.default_label || opt.value || '';
  };

  // Toggle a predefined option on/off
  const toggleVariantOption = (attrCode, optionValue) => {
    setVariantValues(prev => {
      const current = prev[attrCode] || [];
      if (current.includes(optionValue)) {
        return { ...prev, [attrCode]: current.filter(v => v !== optionValue) };
      }
      return { ...prev, [attrCode]: [...current, optionValue] };
    });
  };

  // Load product data when editing
  useEffect(() => {
    if (id) {
      fetch(`/api/products/view/${id}`)
        .then(res => res.json())
        .then(res => {
          if (res.status === 'success' && res.data) {
            setFormData({
              sku: res.data.sku || '',
              category_id: res.data.category_id || '',
              category_name: res.data.category_name || '',
              attribute_set_id: res.data.attribute_set_id || 1,
              item_group_id: res.data.item_group_id || '',
              supplier_id: res.data.supplier_id || '',
              supplier_name: res.data.supplier_name || '',
              attributes: res.data.attributes || {},
              images: res.data.images || [],
            });
            // 编辑模式：使用产品已保存的属性集，不覆盖；添加模式查类目映射
            if (!isEdit && res.data.category_id) {
              lookupAttributeSetByCategory(res.data.category_id);
            } else if (isEdit) {
              setAttributeSetLocked(false);
            }
            // Load combination variants if this is a parent product
            if (res.data.children) {
              // Track original child IDs for computing removed children on save
              const childIds = res.data.children.map(c => c.entity_id);
              originalChildIdsRef.current = new Set(childIds);

              setCombinations(res.data.children.map((c, i) => ({
                id: i,
                _ref: c.entity_id,
                _isExisting: true,
                sku: c.sku,
                name: c.attributes?.name || '',
                cost: c.attributes?.cost || 0,
                gtin: c.attributes?.gtin || '',
                mpn: c.attributes?.mpn || '',
                attributes: c.attributes || {},
                images: c.images || [],
              })));
              // Use saved variant_attributes from DB
              if (res.data.variant_attributes && Array.isArray(res.data.variant_attributes) && res.data.variant_attributes.length > 0) {
                setVariantDefiningAttrs(res.data.variant_attributes.map(code => ({
                  code,
                  label: code, // placeholder; updated below when config loads
                })));
              }
            }
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch(`/api/products/form-config?attribute_set_id=${formData.attribute_set_id}`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') setConfig(res.data);
      })
      .catch(() => {});
  }, [formData.attribute_set_id]);

  // Update variantDefiningAttrs labels when config loads
  useEffect(() => {
    if (config.length > 0 && variantDefiningAttrs.length > 0) {
      setVariantDefiningAttrs(prev => prev.map(v => {
        const matched = config.find(a => a.attribute_code === v.code);
        return matched ? { ...v, label: getLocalizedLabel(matched) } : v;
      }));
    }
  }, [config]);

  const lookupAttributeSetByCategory = async (categoryId) => {
    if (!categoryId) {
      setFormData(prev => ({ ...prev, attribute_set_id: 1 }));
      setAttributeSetLocked(true);
      return;
    }
    try {
      const res = await fetch(`/api/settings/category-attribute-rules/lookup/${categoryId}`);
      const data = await res.json();
      if (data.status === 'success' && data.data?.attribute_set_id) {
        setFormData(prev => ({ ...prev, attribute_set_id: data.data.attribute_set_id }));
      }
    } catch (e) {
      console.error('Failed to lookup attribute set rule:', e);
    }
    setAttributeSetLocked(true);
  };

  const handleAttributeChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [code]: value }
    }));
  };

  const generateCombinations = () => {
    const keys = Object.keys(variantValues);
    if (keys.length === 0) return [];

    const result = [];
    const helper = (index, current) => {
      if (index === keys.length) {
        result.push({ ...current });
        return;
      }
      const key = keys[index];
      const values = variantValues[key] || [];
      values.forEach(val => {
        helper(index + 1, { ...current, [key]: val });
      });
    };

    helper(0, {});
    return result;
  };

  // Calculate variant-defining attributes from existing combinations
  const handleConfigureVariants = () => {
    setShowVariantConfig(true);
  };

  // Compute fingerprints of existing variant combinations to detect duplicates
  const getExistingVariantFingerprints = () => {
    if (variantDefiningAttrs.length === 0) return new Set();
    return new Set(
      combinations
        .map(c => ({
          attrs: c.attributes || {},
          isNewlyAdded: c._newlyAdded,
          productId: c._ref,
        }))
        .filter(item => {
          // Only include combos that have values for all variant-defining attrs
          return variantDefiningAttrs.every(a => item.attrs[a.code]);
        })
        .map(item =>
          variantDefiningAttrs
            .map(a => `${a.code}:${item.attrs[a.code]}`)
            .sort()
            .join('|')
        )
    );
  };

  // Check if a candidate product conflicts with existing variant combos
  const isDuplicateVariant = (p) => {
    if (variantDefiningAttrs.length === 0 || !p.attributes) return true;
    const fingerprints = getExistingVariantFingerprints();
    const candidateFp = variantDefiningAttrs
      .map(a => `${a.code}:${p.attributes[a.code]}`)
      .sort()
      .join('|');
    return fingerprints.has(candidateFp);
  };

  // Validation for adding existing products as variants
  const isProductValid = (p) => {
    if (variantDefiningAttrs.length === 0) return false;
    if (!p.attributes) return false;
    for (const attr of variantDefiningAttrs) {
      const childVal = p.attributes[attr.code];
      if (!childVal || childVal === '') return false;
    }
    // Check that this variant combination doesn't already exist
    if (isDuplicateVariant(p)) return false;
    return true;
  };

  const getProductInvalidReason = (p) => {
    if (variantDefiningAttrs.length === 0) return t('validation.variant_attrs_required');
    if (!p.attributes) return t('validation.variant_attrs_missing');
    for (const attr of variantDefiningAttrs) {
      const childVal = p.attributes[attr.code];
      if (!childVal || childVal === '') {
        return t('validation.variant_attr_missing', {label: attr.label || attr.code});
      }
    }
    if (isDuplicateVariant(p)) return t('validation.variant_duplicate');
    return '';
  };

  const handleNavigateToChild = (entityId, mode) => {
    const path = mode === 'view' ? `/products/view/${entityId}` : `/products/edit/${entityId}`;
    // If variant config is open, close it first to reveal parent form
    setShowVariantConfig(false);
    navigate(path);
  };

  // Generate combinations and return to main form
  const handleGenerateCombinations = () => {
    const combos = generateCombinations();

    // Get image key for each combo based on variantImageAttr
    const newCombinations = combos.map((c, i) => {
      // Copy images for the image-determining attribute value
      let imgs = [];
      if (variantImageAttr && variantImages[c[variantImageAttr]]) {
        imgs = variantImages[c[variantImageAttr]];
      }
      // Build name: parent name, color option label, size option label (comma separated)
      const nameParts = [formData.attributes['name'] || 'Product'];
      variantDefiningAttrs.forEach(a => {
        const optValue = c[a.code];
        if (optValue && variantAttrOptions[a.code]) {
          const opt = variantAttrOptions[a.code].find(o => String(o.value) === String(optValue));
          if (opt) {
            nameParts.push(getOptionLabel(opt, a, lang));
          }
        }
      });
      return {
        id: i,
        attributes: c, // { attr_code: option_value } - variant-defining attrs
        sku: '',
        name: nameParts.join(', '),
        cost: 0,
        gtin: '',
        mpn: '',
        images: imgs,
      };
    });

    setCombinations(newCombinations);
    setShowVariantConfig(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // prevent duplicate submissions

    // 必填项前端验证
    if (!formData.category_id) {
      notify(t('validation.category_required'), 'error');
      return;
    }
    if (!formData.attribute_set_id) {
      notify(t('validation.attribute_set_required'), 'error');
      return;
    }

    // 动态验证所有 is_required=1 的EAV属性 + input_validation 格式验证（使用共享工具）
    // 配置变体时，cost/gtin/mpn/variantDefiningAttrs 移到子产品，父产品无需填写
    const isParentWithVariants = combinations.length > 0 && !formData.item_group_id;
    const hiddenForParentCodes = isParentWithVariants
      ? [...['cost', 'gtin', 'mpn'], ...variantDefiningAttrs.map(v => v.code).filter(Boolean)]
      : [];
    const eavError = findFirstEavError(config, formData.attributes, t, {
      excludeCodes: hiddenForParentCodes,
      getLabel: getLocalizedLabel,
    });
    if (eavError) {
      notify(eavError, 'error');
      return;
    }
    if (!formData.images || !Array.isArray(formData.images) || formData.images.length === 0) {
      notify(t('validation.at_least_one_image'), 'error');
      return;
    }

    try {
      let payload;
      if (isEdit) {
        // Edit mode: update parent + optionally add new children
        payload = {
          ...formData,
          attributes: formData.attributes || {},
          images: formData.images || [],
        };
        if (variantDefiningAttrs.length > 0) {
          payload.variant_attributes = variantDefiningAttrs.map(v => v.code);
        }
        const currentIds = new Set(
          combinations.filter(c => c._isExisting).map(c => c._ref)
        );
        // 计算被移除的子产品 ID
        if (originalChildIdsRef.current.size > 0) {
          const removedIds = [];
          for (const id of originalChildIdsRef.current) {
            if (!currentIds.has(id)) {
              removedIds.push(id);
            }
          }
          if (removedIds.length > 0) {
            payload.remove_children = removedIds;
          }
        }
        const newlyAdded = combinations
          .filter(c => c._newlyAdded && c._ref)
          .map(c => c._ref);
        if (newlyAdded.length > 0) {
          payload.add_children = newlyAdded;
        }
        // 全量更新已有子产品（变体表格中可编辑的 sku + name/cost/gtin/mpn 等 EAV 属性）
        const existingChildren = combinations.filter(c => c._isExisting);
        if (existingChildren.length > 0) {
          payload.update_children = existingChildren.map(c => ({
            entity_id: parseInt(c._ref, 10),
            sku: c.sku,
            attributes: {
              ...(c.attributes || {}),
              name: c.name || '',
              cost: c.cost || 0,
              gtin: c.gtin || '',
              mpn: c.mpn || '',
            },
          }));
        }
        // Don't send images if empty
        if (!payload.images || payload.images.length === 0) {
          delete payload.images;
        }
      } else if (combinations.length > 0) {
        // Create mode with variants: batch array
        payload = [];
        const parentRef = 1;
        payload.push({
          _ref: parentRef,
          ...formData,
          variant_attributes: variantDefiningAttrs.length > 0 ? variantDefiningAttrs.map(v => v.code) : undefined,
        });
        // Batch模式下父产品不应有item_group_id
        if (!payload[0].item_group_id) {
          delete payload[0].item_group_id;
        }
        combinations.forEach((combo, idx) => {
          const childAttrs = {
            ...combo.attributes,
            name: combo.name || '',
            cost: combo.cost || 0,
            gtin: combo.gtin || '',
            mpn: combo.mpn || '',
          };
          payload.push({
            _ref: 100 + idx,
            sku: combo.sku,
            item_group_id: parentRef,
            category_id: formData.category_id,
            attribute_set_id: formData.attribute_set_id,
            attributes: childAttrs,
            images: combo.images,
          });
        });
      } else {
        // Single product creation
        payload = { ...formData };
        // Remove item_group_id if empty (simple products should not have it)
        if (!payload.item_group_id) {
          delete payload.item_group_id;
        }
      }

      setIsSubmitting(true);
      const res2 = await fetch(isEdit ? `/api/products/edit/${id}` : '/api/products/add', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res2.json();

      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        // 回到列表时保留来源页码（从编辑按钮带过来的 state）
        const { fromPage, fromPageSize } = location.state || {};
        const qs = new URLSearchParams();
        if (fromPage && fromPage > 1) qs.set('page', fromPage);
        if (fromPageSize && fromPageSize !== 20) qs.set('pageSize', fromPageSize);
        const q = qs.toString();
        navigate('/products' + (q ? '?' + q : ''));
      } else {
        // 显示具体的字段级错误信息
        let errorMsg = data.message;
        if (data.errors && Object.keys(data.errors).length > 0) {
          const details = Object.entries(data.errors)
            .map(([field, msg]) => `${field}: ${msg}`)
            .join('\n');
          errorMsg += '\n' + details;
        }
        notify(t('common.error') + ': ' + errorMsg, 'error');
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center p-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{isEdit ? (viewOnlyMode ? t('products.view_product') : t('products.edit_product')) : t('products.new_product')}</h1>
        </div>
        {!showVariantConfig && (
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
            <X size={20} />
          </button>
        )}
      </div>

      {!showVariantConfig ? (
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('products.category')} <span className="text-red-500">*</span>
            </label>
            <CategorySelector
              value={formData.category_id}
              onChange={val => {
                setFormData({...formData, category_id: val});
                if (!isEdit) {
                  lookupAttributeSetByCategory(val);
                }
              }}
              viewOnly={viewOnlyMode}
              selectedName={formData.category_name}
            />
          </div>

          <div className="space-y-6">
            {combinations.length === 0 && !formData.item_group_id && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('products.sku_label')}</label>
              <input
                className={`w-full p-2 border ${skuError ? 'border-red-400 dark:border-red-500' : 'border-slate-300 dark:border-slate-700'} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent`}
                value={formData.sku}
                onChange={e => { setSkuError(''); setFormData({...formData, sku: e.target.value}); }}
                onBlur={async () => {
                  if (!formData.sku?.trim() || isEdit) return;
                  setSkuChecking(true);
                  setSkuError('');
                  try {
                    const res = await fetch('/api/products/check-sku?sku=' + encodeURIComponent(formData.sku.trim()));
                    const data = await res.json();
                    if (data.status === 'success' && data.exists) {
                      setSkuError('SKU already exists');
                    }
                  } catch (e) { /* ignore */ }
                  setSkuChecking(false);
                }}
                readOnly={viewOnlyMode}
              />
              {skuError && <p className="text-xs text-red-500 mt-1">{skuError}</p>}
            </div>
          )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('products.attribute_set')} <span className="text-red-500">*</span>
                {attributeSetLocked && formData.category_id && (
                  <span className="ml-2 text-xs text-amber-600 dark:text-amber-400 font-normal">
                    ({t('products.attribute_set_locked')})
                  </span>
                )}
              </label>
              <AttributeSetSelector
                entityType="product"
                value={formData.attribute_set_id}
                onChange={val => setFormData({...formData, attribute_set_id: val})}
                disabled={attributeSetLocked}
              />
            </div>
          </div>

          {/* 供应商（选填） */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('products.supplier')}</label>
            {viewOnlyMode ? (
              <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
                {formData.supplier_id && formData.supplier_name ? (
                  <span className="text-slate-700 dark:text-slate-300 text-sm">{formData.supplier_name}</span>
                ) : formData.supplier_id ? (
                  <span className="text-slate-700 dark:text-slate-300 text-sm">#{formData.supplier_id}</span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
                )}
              </div>
            ) : (
              <SupplierSelect
                value={formData.supplier_id}
                onChange={val => setFormData({...formData, supplier_id: val})}
              />
            )}
          </div>

          {/* 产品名称 (extracted from EAV attributes) */}
          {(() => {
            const nameAttr = config.find(attr => attr.attribute_code === 'name');
            return nameAttr ? (
              <div className="space-y-1">
                <EavAttributeField
                  attr={nameAttr}
                  value={formData.attributes['name'] || ''}
                  onChange={(code, val) => handleAttributeChange(code, val)}
                  viewOnly={viewOnlyMode}
                />
              </div>
            ) : null;
          })()}

          {/* 配置变体按钮：创建时显示，编辑时仅父产品（有变体）显示 */}
          {!viewOnlyMode && (!isEdit || combinations.length > 0) && (
            <div className="flex justify-start gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleConfigureVariants}
                className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={isEdit}
              >
                <Settings2 size={16} />
                {t('products.configure_variants')}
              </button>
              {isEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setShowAddVariants(true);
                    setSelectedAddProductIds([]);
                    (async () => {
                      if (!formData.category_id) return;
                      try {
                        const res = await fetch(`/api/products/simple-candidates?category_id=${formData.category_id}&attribute_set_id=${formData.attribute_set_id}&limit=100`);
                        const data = await res.json();
                        if (data.status === 'success') setAvailableProducts(data.data || []);
                      } catch (e) {}
                    })();
                  }}
                  className="w-full md:w-auto px-4 py-2 bg-emerald-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-emerald-700 transition-all shadow-sm"
                >
                  <Plus size={16} />
                  {t('products.add_existing_variants')}
                </button>
              )}
              {isEdit && combinations.length > 0 && hasPermission('product', 'update_variant_attributes') && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUpdateAttrs(variantDefiningAttrs.map(a => a.code));
                    setShowUpdateVariantAttrs(true);
                  }}
                  className="w-full md:w-auto px-4 py-2 bg-violet-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-violet-700 transition-all shadow-sm"
                >
                  <Settings2 size={16} />
                  {t('products.update_variant_attrs') || '更新变体属性'}
                </button>
              )}
            </div>
          )}

          {/* 变体属性标签 + 未知警告 */}
          {combinations.length > 0 && variantDefiningAttrs.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('products.variant_attrs') || '变体属性'}:
              </span>
              {variantDefiningAttrs.map(a => (
                <span key={a.code} className="px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full border border-blue-200 dark:border-blue-800">
                  {a.label}
                </span>
              ))}
            </div>
          )}
          {combinations.length > 0 && variantDefiningAttrs.length === 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <AlertTriangle size={16} className="text-amber-500 shrink-0" />
              <div className="text-sm text-amber-700 dark:text-amber-400">
                <p className="font-medium">{t('products.variant_attrs_unknown') || '变体属性未知'}</p>
                <p className="text-xs mt-0.5 opacity-80">{t('products.variant_attrs_unknown_hint') || '此产品的子产品数量不足，无法自动推断变体属性。如需要添加更多变体，请先打开变体配置选择属性。'}</p>
              </div>
              <button type="button" onClick={handleConfigureVariants} disabled={isEdit}
                className="ml-auto px-3 py-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
                <Settings2 size={12} className="inline mr-1" />
                {t('products.configure_variants_short') || '配置变体'}
              </button>
            </div>
          )}

          {/* 变体组合表格 (紧跟在配置变体按钮下方) */}
          {combinations.length > 0 && (() => {
            const nameAttr = config.find(a => a.attribute_code === 'name');
            const costAttr = config.find(a => a.attribute_code === 'cost');
            const currencyUnit = costAttr?.currency_unit || costAttr?.default_currency || '';
            return (
            <div className="space-y-3">
              <h3 className="text-lg font-medium">{t('products.variant_combinations')} ({combinations.length})</h3>
              <div className="overflow-x-auto border dark:border-slate-700 rounded-xl shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
                    <tr>
                      <th className="p-3 font-medium whitespace-nowrap w-14" style={{width:56,minWidth:56}}>{t('products.image')}</th>
                      <th className="p-3 font-medium whitespace-nowrap">{nameAttr ? getLocalizedLabel(nameAttr) : 'Name'}</th>
                      <th className="p-3 font-medium whitespace-nowrap">{t('products.sku')}</th>
                      <th className="p-3 font-medium whitespace-nowrap">GTIN</th>
                      <th className="p-3 font-medium whitespace-nowrap">MPN</th>
                      <th className="p-3 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{costAttr ? getLocalizedLabel(costAttr) : 'Cost'}</span>
                          {currencyUnit && <span className="text-xs text-slate-400 dark:text-slate-500 ml-1">({currencyUnit})</span>}
                          {combinations.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const firstCost = combinations[0].cost;
                                setCombinations(combinations.map(c => ({ ...c, cost: firstCost })));
                              }}
                              className="px-2 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-md hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                              title={t('products.apply_cost_all')}
                            >
                              ↓↓
                            </button>
                          )}
                        </div>
                      </th>
                      {variantDefiningAttrs.map(a => (
                        <th key={a.code} className="p-3 font-medium whitespace-nowrap text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{a.label}</th>
                      ))}
                      <th className="p-3 font-medium w-16">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-700">
                    {combinations.map((combo, i) => {
                      const firstImage = combo.images && combo.images.length > 0 ? combo.images[0] : null;
                      const isExistingChild = combo._isExisting === true;
                      return (
                      <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="p-2 text-center" style={{width:56,minWidth:56}}>
                          {firstImage ? (
                            <img src={firstImage} alt="" className="w-10 h-10 object-cover rounded border border-slate-200 dark:border-slate-700" />
                          ) : (
                            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-700"></div>
                          )}
                        </td>
                        <td className="p-2">
                          {viewOnlyMode ? (
                            <span className="text-sm px-1.5 py-1.5 block">{combo.name}</span>
                          ) : (
                            <input
                              className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                              value={combo.name}
                              onChange={e => {
                                const next = [...combinations];
                                next[i] = { ...next[i], name: e.target.value };
                                setCombinations(next);
                              }}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {viewOnlyMode ? (
                            <span className="text-sm px-1.5 py-1.5 block">{combo.sku}</span>
                          ) : (
                            <input
                              className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                              value={combo.sku}
                              onChange={e => {
                                const next = [...combinations];
                                next[i] = { ...next[i], sku: e.target.value };
                                setCombinations(next);
                              }}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {viewOnlyMode ? (
                            <span className="text-sm px-1.5 py-1.5 block">{combo.gtin || ''}</span>
                          ) : (
                            <input
                              className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                              value={combo.gtin || ''}
                              onChange={e => {
                                const next = [...combinations];
                                next[i] = { ...next[i], gtin: e.target.value };
                                setCombinations(next);
                              }}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {viewOnlyMode ? (
                            <span className="text-sm px-1.5 py-1.5 block">{combo.mpn || ''}</span>
                          ) : (
                            <input
                              className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                              value={combo.mpn || ''}
                              onChange={e => {
                                const next = [...combinations];
                                next[i] = { ...next[i], mpn: e.target.value };
                                setCombinations(next);
                              }}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {viewOnlyMode ? (
                            <span className="text-sm px-1.5 py-1.5 block">{combo.cost}{currencyUnit && <span className="text-xs text-slate-400 dark:text-slate-500 ml-1">{currencyUnit}</span>}</span>
                          ) : (
                            <div className="flex items-center gap-1">
                              <input
                                className="p-1.5 border dark:border-slate-700 rounded w-full bg-transparent text-sm"
                                type="number"
                                min="0"
                                step="0.01"
                                value={combo.cost}
                                onChange={e => {
                                  const next = [...combinations];
                                  next[i] = { ...next[i], cost: parseFloat(e.target.value) || 0 };
                                  setCombinations(next);
                                }}
                              />
                              {currencyUnit && <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">{currencyUnit}</span>}
                            </div>
                          )}
                        </td>
                        {variantDefiningAttrs.map(a => (
                          <td key={a.code} className="p-2">
                            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md">
                              {(() => { const v = combo.attributes[a.code]; const o = variantAttrOptions[a.code]?.find(opt => String(opt.value) === String(v)); return o ? o.label : (v || '-'); })()}
                            </span>
                          </td>
                        ))}
                        <td className="p-2 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {isExistingChild && (
                              <>
                                <button type="button" onClick={() => handleNavigateToChild(combo._ref, 'view')}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                  title={t('common.view')}>
                                  <Eye size={15} />
                                </button>
                                {!viewOnlyMode && (
                                  <button type="button" onClick={() => handleNavigateToChild(combo._ref, 'edit')}
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                                    title={t('common.edit')}>
                                    <Edit3 size={15} />
                                  </button>
                                )}
                              </>
                            )}
                            {!viewOnlyMode && (
                              <button
                                type="button"
                                onClick={() => setCombinations(combinations.filter((_, idx) => idx !== i))}
                                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          <ImageUpload
            images={formData.images}
            onImagesChange={imgs => setFormData({...formData, images: imgs})}
            label={t('products.main_image')}
            entityType="product"
            viewOnly={viewOnlyMode}
            required
          />

          {/* EAV属性分组（可折叠） — 复用 EavGroup 组件保持一致样式 */}
          {(() => {
            const filtered = config.filter(attr =>
              attr.attribute_code !== 'name' &&
              (combinations.length > 0 && !formData.item_group_id
                ? !['cost', 'gtin', 'mpn'].includes(attr.attribute_code)
                : true) &&
              (combinations.length > 0 && !formData.item_group_id
                ? !variantDefiningAttrs.find(v => v.code === attr.attribute_code)
                : true)
            );

            const groups = {};
            const getGroupLabel = (attr) => {
              // Use existing group_labels translation system (locale keys: 'zh', 'en')
              const labels = attr.group_labels || {};
              if (labels[lang]) return labels[lang];
              return attr.group_label || 'Other';
            };

            filtered.forEach(attr => {
              const label = getGroupLabel(attr);
              if (!groups[label]) {
                groups[label] = {
                  label,
                  sortOrder: attr.group_sort_order ?? 999,
                  attrs: [],
                };
              }
              groups[label].attrs.push(attr);
            });

            const sortedGroups = Object.values(groups).sort((a, b) => a.sortOrder - b.sortOrder);

            return sortedGroups.map(group => (
              <EavGroup
                key={group.label}
                groupLabel={group.label}
                attributes={group.attrs}
                values={formData.attributes}
                onAttributeChange={(code, val) => handleAttributeChange(code, val)}
                viewOnly={viewOnlyMode}
                showCount
                isCollapsed={collapsedGroups.has(group.label)}
                onToggle={() => setCollapsedGroups(prev => {
                  const next = new Set(prev);
                  if (next.has(group.label)) next.delete(group.label);
                  else next.add(group.label);
                  return next;
                })}
              />
            ));
          })()}

          {!viewOnlyMode && (
            <div className="flex justify-end gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  isSubmitting
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Save size={18} />
                )}
                {isSubmitting ? t('common.processing') : (isEdit ? t('common.update') : t('common.add'))}
              </button>
            </div>
          )}
        </form>
      ) : (
        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setShowVariantConfig(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-xl font-bold">{t('products.variant_detail_config')}</h2>
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">1. {t('products.select_variant_attrs')}</h3>
              {/* 搜索框下拉多选 */}
              <div className="relative" ref={dropdownRef}>
                <div
                  className="flex items-center gap-2 p-2 border border-slate-300 dark:border-slate-700 rounded-lg cursor-pointer bg-white dark:bg-slate-800 hover:border-blue-400 transition-colors"
                  onClick={() => setVariantDropdownOpen(!variantDropdownOpen)}
                >
                  <Search size={16} className="text-slate-400 shrink-0" />
                  <div className="flex-1 flex flex-wrap gap-1 min-h-[24px]">
                    {variantDefiningAttrs.length > 0 ? (
                      variantDefiningAttrs.map(attr => (
                        <span key={attr.code} className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs rounded-md">
                          {attr.label}
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); setVariantDefiningAttrs(prev => prev.filter(v => v.code !== attr.code)); }}
                            className="hover:text-red-500"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">{t('common.select')}</span>
                    )}
                  </div>
                  <ChevronDown size={16} className={`text-slate-400 transition-transform ${variantDropdownOpen ? 'rotate-180' : ''}`} />
                </div>

                {variantDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
                    {/* 搜索输入框 */}
                    <div className="p-2 border-b dark:border-slate-700">
                      <div className="flex items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-700 rounded-lg">
                        <Search size={14} className="text-slate-400 ml-1" />
                        <input
                          className="flex-1 bg-transparent outline-none text-sm"
                          placeholder={t('common.search')}
                          value={variantSearch}
                          onChange={e => setVariantSearch(e.target.value)}
                          autoFocus
                        />
                        {variantSearch && (
                          <button type="button" onClick={() => setVariantSearch('')} className="text-slate-400 hover:text-slate-600 mr-1">
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* 下拉选项列表 */}
                    <div className="max-h-60 overflow-y-auto p-1">
                      {config.filter(attr => {
                        const label = getLocalizedLabel(attr).toLowerCase();
                        const code = attr.attribute_code?.toLowerCase() || '';
                        const query = variantSearch.toLowerCase();
                        return !query || label.includes(query) || code.includes(query);
                      }).length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">{t('common.no_data')}</p>
                      ) : (
                        config.filter(attr => {
                          const label = getLocalizedLabel(attr).toLowerCase();
                          const code = attr.attribute_code?.toLowerCase() || '';
                          const query = variantSearch.toLowerCase();
                          return !query || label.includes(query) || code.includes(query);
                        }).map(attr => {
                          const isSelected = !!variantDefiningAttrs.find(v => v.code === attr.attribute_code);
                          return (
                            <label
                              key={attr.attribute_id}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                className="rounded"
                                onChange={e => {
                                  if (e.target.checked) {
                                    setVariantDefiningAttrs([...variantDefiningAttrs, { code: attr.attribute_code, label: getLocalizedLabel(attr) }]);
                                  } else {
                                    setVariantDefiningAttrs(variantDefiningAttrs.filter(v => v.code !== attr.attribute_code));
                                  }
                                }}
                              />
                              <span className="text-sm">{getLocalizedLabel(attr)}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">2. {t('products.define_attribute_values')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {variantDefiningAttrs.map(attr => {
                  const options = variantAttrOptions[attr.code];
                  const selected = variantValues[attr.code] || [];
                  return (
                    <div key={attr.code} className="space-y-2 p-4 border rounded-xl bg-slate-50 dark:bg-slate-800/50">
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-300">{getLocalizedLabel(attr)}</label>
                      {options === null ? (
                        <p className="text-sm text-slate-400">{t('common.loading')}</p>
                      ) : options && options.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {options.map(opt => {
                            const isSelected = selected.includes(opt.value);
                            const isSwatch = opt.swatch_type === 'color' && opt.swatch_value;
                            return (
                              <div key={opt.option_id} className="relative group">
                                <button
                                  type="button"
                                  onClick={() => toggleVariantOption(attr.code, opt.value)}
                                  className={`transition-all ${
                                    isSwatch
                                      ? `p-0.5 rounded-md border ${
                                          isSelected
                                            ? 'border-blue-500 shadow-md ring-2 ring-blue-500/30'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:scale-110'
                                        }`
                                      : `px-3 py-1.5 rounded-lg text-sm border ${
                                          isSelected
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:text-blue-600'
                                        }`
                                  }`}
                                >
                                  {isSwatch ? (
                                    <div
                                      className="w-9 h-9 rounded-md shadow-sm border border-slate-200 dark:border-slate-600"
                                      style={{ backgroundColor: opt.swatch_value }}
                                    />
                                  ) : (
                                    opt.label
                                  )}
                                </button>
                                {/* Hover tooltip for swatch type */}
                                {isSwatch && (
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
                                    {opt.label}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800 dark:border-t-slate-700"></div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-600">{t('products.no_options_available')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">3. {t('products.variant_images_upload')}</h3>

              {/* 选择用于区分图片的属性 */}
              {variantDefiningAttrs.length > 0 ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('products.select_image_attr')}
                  </label>
                  <select
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-slate-800"
                    value={variantImageAttr}
                    onChange={e => setVariantImageAttr(e.target.value)}
                  >
                    <option value="">{t('common.select')}</option>
                    {variantDefiningAttrs.map(attr => (
                      <option key={attr.code} value={attr.code}>{attr.label}</option>
                    ))}
                  </select>
                  {variantImageAttr && (
                    <p className="text-xs text-slate-500">
                      {t('products.image_attr_hint')}
                    </p>
                  )}
                </div>
              ) : null}

              {/* 列出每个属性值的图片上传 */}
              {variantImageAttr && variantAttrOptions[variantImageAttr] ? (
                <div className="space-y-6 mt-4">
                  {variantAttrOptions[variantImageAttr]
                    .filter(opt => variantValues[variantImageAttr]?.includes(opt.value))
                    .map(opt => (
                      <div key={opt.option_id} className="p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
                          {opt.label}
                        </label>
                        <ImageUpload
                          images={variantImages[opt.value] || []}
                          onImagesChange={(newImages) => {
                            setVariantImages(prev => ({ ...prev, [opt.value]: newImages }));
                          }}
                          entityType="product"
                          label={t('products.variant_image_for', { value: opt.label })}
                          viewOnly={viewOnlyMode}
                        />
                      </div>
                    ))}
                </div>
              ) : variantImageAttr ? (
                <p className="text-sm text-slate-400">{t('common.loading')}</p>
              ) : null}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleGenerateCombinations}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Settings2 size={16} />
              {t('products.generate_combinations')}
            </button>
          </div>
        </div>
      )}

      {/* 添加已有产品作为变体的弹窗 */}
      {/* 更新变体属性弹窗 */}
      {showUpdateVariantAttrs && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowUpdateVariantAttrs(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b dark:border-slate-700">
              <h2 className="text-xl font-bold">{t('products.update_variant_attrs') || '更新变体属性'}</h2>
              <button onClick={() => setShowUpdateVariantAttrs(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                {t('products.update_variant_attrs_hint') || '已选的变体属性不可取消。勾选新增的属性后保存，系统将自动验证所有子产品是否包含新属性的值。'}
              </p>
              {config.map(attr => {
                const code = attr.attribute_code;
                const isExisting = variantDefiningAttrs.some(v => v.code === code);
                const isSelected = selectedUpdateAttrs.includes(code);
                // Exclude non-option-based attributes (same filter as variant config)
                if (!['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(attr.input_type || attr.backend_type || '')) return null;
                return (
                  <label key={code} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${isExisting ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isExisting}
                      className="rounded"
                      onChange={e => {
                        if (isExisting) return;
                        setSelectedUpdateAttrs(prev =>
                          e.target.checked
                            ? [...prev, code]
                            : prev.filter(c => c !== code)
                        );
                      }}
                    />
                    <span className="text-sm">{getLocalizedLabel(attr)}</span>
                    {isExisting && (
                      <span className="ml-auto text-xs text-blue-500">{t('products.already_selected') || '已选'}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-slate-700">
              <button
                type="button"
                onClick={() => setShowUpdateVariantAttrs(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/products/${id}/variant-config/add`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token'),
                      },
                      body: JSON.stringify({ variant_attributes: selectedUpdateAttrs }),
                    });
                    const data = await res.json();
                    if (data.status === 'success') {
                      const newDefiningAttrs = data.data.variant_attributes.map(code => ({
                        code,
                        label: (config.find(a => a.attribute_code === code)?.default_label || code),
                      }));
                      setVariantDefiningAttrs(newDefiningAttrs);
                      setShowUpdateVariantAttrs(false);
                      notify(t('products.update_variant_attrs_success') || '变体属性更新成功', 'success');
                    } else {
                      notify(data.message || t('common.update_failed'), 'error');
                    }
                  } catch (e) {
                    notify(t('common.network_error') || '网络错误', 'error');
                  }
                }}
                className="px-6 py-2 bg-violet-600 text-white rounded-lg flex items-center gap-2 hover:bg-violet-700 transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddVariants && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddVariants(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b dark:border-slate-700">
              <h2 className="text-xl font-bold">{t('products.add_existing_variants')}</h2>
              <button onClick={() => setShowAddVariants(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* 候选产品列表 */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    placeholder={t('common.search')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {availableProducts.length === 0 ? (
                <p className="text-center text-slate-500 py-10">{t('common.no_results')}</p>
              ) : (
                <div className="overflow-x-auto border dark:border-slate-700 rounded-xl">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
                      <tr>
                        <th className="p-3 w-10">
                          <input
                            type="checkbox"
                            onChange={e => {
                              if (e.target.checked) {
                                const valid = availableProducts.filter(p => isProductValid(p)).map(p => p.entity_id);
                                setSelectedAddProductIds(valid);
                              } else {
                                setSelectedAddProductIds([]);
                              }
                            }}
                          />
                        </th>
                        <th className="p-3 font-medium">ID</th>
                        <th className="p-3 font-medium">{t('products.sku')}</th>
                        <th className="p-3 font-medium">{t('products.name')}</th>
                        {variantDefiningAttrs.map(a => (
                          <th key={a.code} className="p-3 font-medium">{a.label}</th>
                        ))}
                        <th className="p-3 font-medium text-center">{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {(() => {
                        const q = searchQuery.toLowerCase();
                        const filtered = availableProducts.filter(p => {
                          if (!q) return true;
                          const attrs = Object.values(p.attributes || {}).join(' ').toLowerCase();
                          return p.sku?.toLowerCase().includes(q) || String(p.entity_id).includes(q) || attrs.includes(q);
                        });
                        return filtered.map(p => {
                          const valid = isProductValid(p);
                          const selected = selectedAddProductIds.includes(p.entity_id);
                          return (
                            <tr key={p.entity_id}
                              className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer ${!valid ? 'opacity-50' : ''}`}
                              onClick={() => {
                                if (!valid) return;
                                if (selected) {
                                  setSelectedAddProductIds(prev => prev.filter(id => id !== p.entity_id));
                                } else {
                                  setSelectedAddProductIds(prev => [...prev, p.entity_id]);
                                }
                              }}
                            >
                              <td className="p-3" onClick={e => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  disabled={!valid}
                                  onChange={() => {
                                    if (!valid) return;
                                    if (selected) {
                                      setSelectedAddProductIds(prev => prev.filter(id => id !== p.entity_id));
                                    } else {
                                      setSelectedAddProductIds(prev => [...prev, p.entity_id]);
                                    }
                                  }}
                                />
                              </td>
                              <td className="p-3 text-slate-500">{p.entity_id}</td>
                              <td className="p-3">{p.sku}</td>
                              <td className="p-3">{p.attributes?.name || ''}</td>
                              {variantDefiningAttrs.map(a => (
                                <td key={a.code} className="p-3">{p.attributes?.[a.code] || '-'}</td>
                              ))}
                              <td className="p-3 text-center">
                                {valid ? (
                                  <span className="text-xs px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full">{t('common.available')}</span>
                                ) : (
                                  <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full flex items-center gap-1 justify-center" title={getProductInvalidReason(p)}>
                                    <AlertCircle size={12} />
                                    {t('common.incompatible')}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 底部按钮 */}
            <div className="flex items-center justify-between p-6 border-t dark:border-slate-700">
              <span className="text-sm text-slate-500">
                {t('products.selected_count')} {selectedAddProductIds.length} {t('common.items')}
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddVariants(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Add selected products as existing children
                    const existingIds = new Set(combinations.filter(c => c._isExisting).map(c => c._ref));
                    const newVariants = availableProducts
                      .filter(p => selectedAddProductIds.includes(p.entity_id))
                      .filter(p => !existingIds.has(p.entity_id));

                    const newCombos = newVariants.map((p, i) => ({
                      id: combinations.length + i,
                      _ref: p.entity_id,
                      _isExisting: true,
                      _newlyAdded: true,
                      sku: p.sku || '',
                      name: p.attributes?.name || '',
                      cost: p.attributes?.cost || 0,
                      gtin: p.attributes?.gtin || '',
                      mpn: p.attributes?.mpn || '',
                      attributes: p.attributes || {},
                      images: [],
                    }));

                    setCombinations(prev => [...prev, ...newCombos]);
                    setShowAddVariants(false);
                    notify(`${newCombos.length} ${t('common.items_added')}`, 'success');
                  }}
                  disabled={selectedAddProductIds.length === 0}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg flex items-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  <Plus size={16} />
                  {t('products.add_selected')} ({selectedAddProductIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductForm;
